import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import formatDecimal from "../../helpers/formatDecimal";
import {
  Box,
  TextField,
  Typography,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BreadCrumbs from "./BreadCrumbs";
import ModalityCard from "./ModalityCard";
import ErrorMsg from "./ErrorMsg";
import BackButton from "./BackButton";
import PayButton from "./PayButton";
import { postPayment, postPaypalPayment } from "../../services/signUp";
import { coursesTags, urlToTag } from "../../context/tags";
import { useCourses } from "../../context/CourseContext";
import { applyBootcamp } from "../../helpers/coursesInscription";
import { LanguageContext } from "../../context/langContext";
import { useContext } from "react";
import { track } from "../../helpers/tracker";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme, isMobile) => ({
  container: {
    width: { xs: "80%", md: "70%", lg: "65%", xl: "65%" },
    minHeight: "25vh",
    backgroundSize: "cover",
    color: theme.palette.blue.main,
    paddingBottom: isMobile ? "50px" : "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: isMobile ? "flex-start" : "center",
  },
  inputsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: isMobile ? "center" : "flex-start",
  },
  input: {
    width: "100%",
    marginTop: "36px",
    boxShadow: "0px 2.5px 2.5px rgba(0, 31, 85, 0.16)",
    borderRadius: "56px",
  },
  inputText: {
    padding: "16px 32px",
    color: theme.palette.blue.main,
    fontSize: isMobile ? "21px" : "26px",
    fontFamily: theme.typography.montserrat.semibold,
    border: "none",
    "& ::placeholder": {
      color: theme.palette.blue.main,
      opacity: "1 !important",
    },
  },
  questions: {
    marginTop: "48px",
    marginLeft: isMobile ? "0px" : "32px",
    width: "100%",
    display: "flex",
  },
  divider: {
    marginTop: isMobile ? "32px" : "32px",
    width: isMobile ? "100%" : "110%",
    background: "#cbc8c8",
  },
  total: {
    marginTop: "48px",
    marginRight: isMobile ? "0px" : "32px",
    width: "100%",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: isMobile ? "center" : "flex-end",
  },
  buttons: {
    marginTop: "48px",
    display: "flex",
    width: "100%",
    flexDirection: isMobile ? "column-reverse" : "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  typography: {
    finish: {
      fontSize: isMobile ? "30px" : { xl: "32px", lg: "32px", md: "32px" },
      fontFamily: theme.typography.montserrat.bold,
      marginTop: "48px",
    },
    about: {
      fontFamily: theme.typography.montserrat.regular,
      fontSize: isMobile ? "20px" : "22px",
      textAlign: isMobile ? "center" : "left",
      marginTop: "34px",
    },
    questions: {
      textAlign: "left",
      marginLeft: isMobile ? "8px" : "8px",
      fontStyle: "italic",
    },
    total: {
      fontFamily: theme.typography.montserrat.regular,
      fontSize: "26px",
      textAlign: isMobile ? "center" : "right",
    },
  },
});

const Form = (url) => {
  const isMobile = useMediaQuery("(max-width:899px)");
  const { atrCourse } = useCourses();
  const theme = useTheme();
  const classes = useStyles(theme, isMobile);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [data, setData] = useState(() =>
    JSON.parse(localStorage.getItem("data"))
  );
  const [source] = useState(() => localStorage.getItem("utm_source"));

  const location = useLocation();
  const language = useContext(LanguageContext);
  const {
    unfilledFieldsError,
    notCoincidentEmailError,
    invalidEmailError,
    title,
    helper,
    total,
    payButtonText,
  } = useTranslation(location.pathname, "Form");

  useEffect(() => {
    if (location.search.includes("atr")) {
      if (!data || data.cohortLabel !== coursesTags.INTRO_JAVASCRIPT_ATR) {
        setData(atrCourse);
        localStorage.setItem("data", JSON.stringify(atrCourse));
      }
    } else {
      let course = location.pathname.split("/")[2];
      if (data?.cohortLabel.split("-")[0] !== urlToTag[course]) {
        navigate(`/aplica/${course}/fechas`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  function formatCurrency() {
    let currency;
    if (language === "es")
      currency = `ARS ${
        isNaN(formatDecimal(data.priceARS)) ? "" : formatDecimal(data.priceARS)
      }`;
    else
      currency = `USD ${
        isNaN(formatDecimal(data.priceUSD)) ? "" : formatDecimal(data.priceUSD)
      }`;
    return currency;
  }

  const [error, setShowError] = useState("");
  const [form, setForm] = useState({
    email: "",
    confirm: "",
  });

  const validateInputs = () => {
    if (!form.email && !form.confirm) return setShowError(unfilledFieldsError);

    if (form.email && form.confirm && form.email !== form.confirm)
      return setShowError(notCoincidentEmailError);

    const emailRegex =
      /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

    if (!emailRegex.test(form.email)) return setShowError(invalidEmailError);

    setShowError("");
    return true;
  };

  const handleForm = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value.trim() });
    e.preventDefault();
  };

  const handleSubmit = async () => {
    const returnURL = process.env.REACT_APP_FRONT_RETURN_URL;
    const paypalReturnURL = process.env.REACT_APP_RETURN_URL_PAYPAL;
    let body = {};
    try {
      if (validateInputs() && location.pathname.includes("intro")) {
        localStorage.setItem("email", form.email);
        let resp;
        body = {
          title: data.type.name,
          quantity: 1,
          unitPrice: parseFloat(
            language === "es" ? data.priceARS : data.priceUSD
          ),
          total: language === "es" ? data.priceARS : data.priceUSD,
          successURL:
            language === "es"
              ? `${returnURL}?operation=success`
              : paypalReturnURL,
          failureURL: `${returnURL}?operation=rejected`,

          email: form.email,
          currency: language === "es" ? "ARS" : "USD",
          discount: 0,
          ...data,
          cohort_label: data.cohortLabel,
          source,
        };

        if (language === "es") {
          body["autoReturn"] = true;
          setLoading(true);
          resp = await postPayment(body);
        } else {
          setLoading(true);
          resp = await postPaypalPayment(body);
        }
        track(data.cohortLabel.split("-")[0]);
        const paymentLink = resp.data;
        window.location.replace(paymentLink);
      } else if (validateInputs()) {
        setLoading(true);
        await applyBootcamp(data, form.email);
        track(data.cohortLabel.split("-")[0]);
        setLoading(false);
        navigate(
          "/aplicar/payment?operation=success&collection_status=approved"
        );
      }
    } catch (err) {
      setLoading(false);
      navigate("/aplicar/payment?operation=rejected");
    }
  };

  return (
    <Box sx={classes.container}>
      {data ? (
        <>
          <BreadCrumbs />
          <ModalityCard data={data} />
          <Box sx={classes.inputsContainer}>
            <Typography sx={classes.typography.finish}>{title}</Typography>
            <ErrorMsg error={error} />
            <TextField
              name="email"
              variant="standard"
              placeholder="EMAIL*:"
              value={form.email}
              onChange={(e) => handleForm(e)}
              sx={classes.input}
              InputProps={{
                disableUnderline: true,
                sx: classes.inputText,
              }}
              onBlur={validateInputs}
            />
            <TextField
              name="confirm"
              variant="standard"
              placeholder="CONFIRMAR EMAIL*:"
              value={form.confirm}
              onChange={(e) => handleForm(e)}
              sx={classes.input}
              InputProps={{
                disableUnderline: true,
                sx: classes.inputText,
              }}
              onBlur={validateInputs}
            />
          </Box>
          <Box sx={classes.questions}>
            <InfoOutlinedIcon />
            <Typography sx={classes.typography.questions}>{helper}</Typography>
          </Box>
          <Divider sx={classes.divider} />
          {location.pathname.includes("intro") ? (
            <Box sx={classes.total}>
              <Typography sx={classes.typography.total}>
                {total}&nbsp;
              </Typography>
              <Typography sx={classes.typography.total}>
                <b>{data ? formatCurrency() : "$"}</b>
              </Typography>
            </Box>
          ) : null}
          <Box sx={classes.buttons}>
            <BackButton />
            <PayButton
              loading={loading}
              disabled={
                !!error ||
                !form.email ||
                !form.confirm ||
                form.email !== form.confirm ||
                loading
              }
              text={
                location.pathname.includes("intro")
                  ? `${payButtonText.title.intro[0]} ${
                      language === "es"
                        ? payButtonText.title.intro[1].arg
                        : payButtonText.title.intro[1].rdm
                    }`
                  : payButtonText.title.bootcamp
              }
              handleSubmit={handleSubmit}
              form={form}
            />
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default Form;
